<template>
  <VeeForm class="view-case" as="div" ref="form" :validation-schema="validationSchema">
    <div class="case-container">
      <div v-if="loading" class="loader"></div>
      <div v-else>
        <div class="case-header" data-cy="case-header">
          <div class="case-header-section" data-cy="case-number">
            <h4>{{ $t('viewCase.case_number') }}</h4>
            <span>{{ caseOrder.caseNumber }}</span>
          </div>
          <div v-if="caseOrder && caseOrder.status" class="case-header-section" data-cy="case-status">
            <h4>{{ $t('viewCase.case_status_label') }}</h4>
            <span>{{ $t(`viewCase.case_status.${caseOrder?.status?.toLowerCase()}`) }}</span>
          </div>
          <div class="case-header-section" data-cy="case-creation-date">
            <h4 v-if="caseNotSubmitted">
              {{ $t('viewCase.case_creation_date') }}
            </h4>
            <h4 v-else>
              {{ $t('viewCase.case_submitted_date') }}
            </h4>
            <span>{{ formatDate(caseOrder.createdAt) }}</span>
          </div>
        </div>
        <div v-if="isReturn" class="return-case-cnt">
          <div v-if="!isPickupRequest()" class="return-case-options">
            <div class="return-case-option-item">
              <BackstageInputLabel
                :label="$t('viewCase.return_reason')"
                class="label-request-input"
                required
                :class="{ disabled: !caseNotSubmitted }">
                <VeeField
                  as="select"
                  v-model="caseOrder.reason"
                  :disabled="!caseNotSubmitted"
                  name="return_reason"
                  :class="{
                    'with-arrow': caseNotSubmitted,
                    'option-placeholder': caseOrder.reason === '',
                  }"
                  v-on:change="onChangeCase">
                  <option value="" disabled selected hidden>
                    {{ $t('global.select_placeholder') }}
                  </option>
                  <option v-for="defectReason in defectReasons" :key="defectReason.type" :value="defectReason.type">
                    {{ getTranslationFromTranslateField(defectReason.description) }}
                  </option>
                </VeeField>
              </BackstageInputLabel>
              <BackstageInput
                v-model="caseOrder.parcelCount"
                required
                name="return_parcel_count"
                :disabled="!caseNotSubmitted"
                :label="$t('returnCaseForm.parcels')"
                type="number"
                v-on:change="onChangeCase">
              </BackstageInput>
            </div>
            <div class="return-case-option-item">
              <BackstageInput
                v-model="caseOrder.description"
                type="textarea"
                :label="$t('viewCase.comments')"
                required
                name="return_description"
                class="label-request-textarea"
                :class="{ disabled: !caseNotSubmitted }"
                :disabled="!caseNotSubmitted"
                v-on:change="onChangeCase">
              </BackstageInput>
            </div>
            <div class="return-case-option-item"></div>
          </div>
          <ReturnCaseForm
            v-if="isPickupRequest() && defectReasons.length"
            :defect-reasons="defectReasons"
            :pickup-details="caseOrder.pickup"
            :case-not-submitted="caseNotSubmitted"
            v-on:form-is-valid="updatePickUpData">
          </ReturnCaseForm>
        </div>
        <div v-if="isReturn" class="return-items">
          <ag-grid-vue
            class="ag-theme-balham"
            data-cy="cases-table"
            :grid-options="gridOptions"
            :column-defs="returnColumnDefinitions"
            :default-col-def="defaultColDef"
            :row-data="caseOrder.lines"
            :modules="modules"
            v-on:grid-ready="onGridReady">
          </ag-grid-vue>
        </div>
        <div v-if="isWarranty" class="products-list" data-cy="product-list">
          <!-- ToDo move this to it's own component -->
          <div
            v-for="line in caseOrder.lines"
            :key="line.id"
            class="product"
            :data-cy="'product ' + line.productMainGroup"
            :class="[{ expanded: !line.collapsed }]">
            <div class="product-overview" v-on:click="toggleLine(line)">
              <div class="product-info">
                <div class="image" data-cy="product-image">
                  <img :src="getBoxImageProxyUrl(line.productImageUrl)" />
                </div>
                <div class="info">
                  <h6 data-cy="product-name">
                    {{ line.productGroupName }}
                  </h6>
                  <p data-cy="item-size">
                    {{ line.productFabricSize }}
                  </p>
                  <p data-cy="item-number">
                    {{ line.productNumber }}
                  </p>
                </div>
              </div>
              <div
                v-if="line.collapsed"
                class="customer-name"
                :class="{ alone: !(caseNotSubmitted && caseOrder.lines.length > 1) }"
                data-cy="customer-name-alone">
                <h6>{{ $t('viewCase.customer_name') }}</h6>
                <p>{{ line.customerName || '-' }}</p>
              </div>
              <div v-if="line.collapsed" class="defect-reason" data-cy="defect-reason-alone">
                <h6>{{ $t(`viewCase.defect_reason`) }}</h6>
                <p>{{ selectedDefectReasonDescription(line) }}</p>
              </div>
              <BackstageIcon
                v-if="caseNotSubmitted && caseOrder.lines.length > 1"
                class="delete-line"
                name="trash"
                data-cy="delete-line-button"
                v-on:click.self="deleteLine(line)" />
            </div>
            <div class="product-details" data-cy="product-details-grid">
              <div class="form-container">
                <div class="col">
                  <BackstageInput
                    v-model="line.customerName"
                    data-cy="customer-name"
                    :disabled="!caseNotSubmitted"
                    :label="$t('viewCase.customer_name')"
                    type="text"
                    v-on:input="onChangeLine(line)">
                  </BackstageInput>

                  <!-- ToDo update the combobox to accept options as an object -->
                  <BackstageCombobox
                    v-model="line.defectReason"
                    @click="onChangeLine(line)"
                    v-on:update:modelValue="setDefectType(line)"
                    :disabled="!caseNotSubmitted"
                    :name="`defect_reason:${line.id}`"
                    :label="getFieldTranslationByType(line, TranslationType.LABEL, CaseLineFields.DEFECT_REASON)"
                    :options="getDefectReasonsOptions(line.productMainGroup)"
                    data-cy="defect-reason"
                    options-data-cy="defect-reason-dropdown">
                  </BackstageCombobox>

                  <BackstageInputCalendar
                    :name="`purchase_date:${line.id}`"
                    v-model="line.purchaseDate"
                    v-on:update:modelValue="onChangeLine(line)"
                    class="date-selector"
                    :label="$t('viewCase.purchase_date')"
                    :disabled-dates="disabledDates()"
                    :placeholder="$t('global.datepicker_placeholder')"
                    :read-only="!caseNotSubmitted"
                    data-cy="purchase-date">
                  </BackstageInputCalendar>
                  <span v-if="caseNotSubmitted && !line.purchaseDate" class="validation-error">
                    {{
                      $t('formsValidations.rules_error_messages.required', {
                        field: $t(`formsValidations.fields.purchase_date`),
                      })
                    }}
                  </span>
                </div>
                <div class="col">
                  <!-- Note: Product code and Production code is same -->
                  <BackstageCombobox
                    v-model="line.productCode"
                    @click="onChangeLine(line)"
                    :disabled="!caseNotSubmitted"
                    :name="`product_code:${line.id}`"
                    :options="getProductionCodeOptions(line.productMainGroup)"
                    :label="getFieldTranslationByType(line, TranslationType.LABEL, CaseLineFields.PRODUCT_CODE)"
                    :placeholder="
                      getFieldTranslationByType(line, TranslationType.PLACEHOLDER, CaseLineFields.PRODUCT_CODE)
                    "
                    data-cy="production-code"
                    options-data-cy="production-code-dropdown">
                    <template #tooltip>
                      <BackstageImageTooltip :image-location="getTooltipImageByProduct(line)" />
                    </template>
                  </BackstageCombobox>

                  <BackstageCombobox
                    v-model="line.factoryCode"
                    @click="onChangeLine(line)"
                    :disabled="!caseNotSubmitted"
                    :name="`factory_code:${line.id}`"
                    :label="getFieldTranslationByType(line, TranslationType.LABEL, CaseLineFields.FACTORY_CODE)"
                    :options="getFactoryCodeOptions(line.productMainGroup)"
                    data-cy="factory-code"
                    options-data-cy="factory-code-dropdown">
                    <template #tooltip>
                      <BackstageImageTooltip :image-location="getTooltipImageByProduct(line)" />
                    </template>
                  </BackstageCombobox>

                  <BackstageCombobox
                    v-if="line.productMainGroup === 'shoes'"
                    v-model="line.distanceRun"
                    @click="onChangeLine(line)"
                    :disabled="!caseNotSubmitted"
                    :name="`distance_run:${line.id}`"
                    :label="getFieldTranslationByType(line, TranslationType.LABEL, CaseLineFields.DISTANCE_RUN)"
                    :options="getDistanceRunOptions()"
                    data-cy="distance-run"
                    options-data-cy="distance-run-dropdown">
                  </BackstageCombobox>
                </div>
                <div class="col">
                  <div v-if="line.images?.length || caseNotSubmitted" class="images-group" data-cy="image-upload">
                    <label class="images-label">
                      {{ $t(`viewCase.images_label_${line.productMainGroup?.toLowerCase()}`) }}
                    </label>
                    <div class="image-upload-container">
                      <span v-if="caseNotSubmitted">
                        <CaseLineImageInput
                          v-for="image in line.images"
                          :key="image.imageId"
                          :disabled="!caseNotSubmitted"
                          :model="image"
                          :line="line"
                          :delete="deleteImage"
                          :change="onChangeImage">
                        </CaseLineImageInput>
                        <CaseLineImageInput
                          v-for="index in maxImageCount - (line?.images?.length || 0)"
                          :key="index"
                          :line="line"
                          :change="onChangeImage">
                        </CaseLineImageInput>
                      </span>
                      <span v-else>
                        <CaseLineImageInput
                          v-for="image in line.images"
                          :key="image.imageId"
                          :disabled="!caseNotSubmitted"
                          :model="image">
                        </CaseLineImageInput>
                      </span>
                    </div>
                    <span v-if="!hideImagesErrorMessage(line)" class="validation-error">
                      {{ $t('viewCase.image_validation_error_message') }}
                    </span>
                  </div>
                  <div class="comment-group" data-cy="comments-field">
                    <BackstageTextarea
                      v-model="line.comments"
                      name="comments"
                      :label="$t('viewCase.comments')"
                      :class="{ disabled: !caseNotSubmitted }"
                      :disabled="!caseNotSubmitted"
                      v-on:input="onChangeLine(line)">
                    </BackstageTextarea>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </VeeForm>
</template>

<script lang="ts">
import moment from 'moment';
import { storeToRefs } from 'pinia';
import { defineComponent } from 'vue';
import { useRouter } from 'vue-router';
import { type ObjectShape, object } from 'yup';
import { Form as VeeForm, Field as VeeField } from 'vee-validate';

import { Case } from '@/models/case.model';
import { CaseLine } from '@/models/case-line.model';
import { CaseImage } from '@/models/case-image.model';
import { FactoryCode } from '@/models/factory-code.model';
import { DefectReason } from '@/models/defect-reason.model';
import { CaseLineFields } from '@/models/case-line-fields.enum';
import { ProductMainGroups } from '@/models/product-main-group.enum';
import { CasePickupModel } from '@/models/case-pickup.model';
import { ValidatorResult } from '@/models/validation-result.model';
import type { ProductionCode } from '@/models/production-code.model';
import { returnRequestTypeByLegalEntity } from '@/models/return-request-type-by-region.model';

import { GridApi } from '@ag-grid-community/core';
import { AgGridVue } from '@ag-grid-community/vue3';
import { CasesService } from '@/services/cases.service';
import { DistancesRun } from '@/constants/distances-run.constants';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';

import { useUserStore } from '@/stores/user';
import { useAddEditCaseStore } from '@/stores/add-edit-case';
import { CaseStatus, CaseReturnRequestType, CaseType, LegalEntityEnum, CaseDto } from '@onrunning/backstage-models';

import BackstageIcon from '@/components/common/BackstageIcon.vue';
import BackstageInput from '@/components/common/BackstageInput.vue';
import ReturnCaseForm from '@/components/cases/return-case-form.vue';
import BackstageTextarea from '@/components/common/BackstageTextarea.vue';
import BackstageCombobox from '@/components/common/BackstageCombobox.vue';
import BackstageInputLabel from '@/components/common/BackstageInputLabel.vue';
import CaseLineImageInput from '@/components/cases/case-line-image-input.vue';
import BackstageImageTooltip from '@/components/common/BackstageImageTooltip.vue';
import BackstageInputCalendar from '@/components/common/BackstageInputCalendar.vue';

import { useGrid } from '../composables/grid';
import { useLocale } from '@/composables/locale';
import { useFormat } from '../composables/format';
import { useFormValidation } from '@/composables/form-validation';
import { useBPLogger } from '@/composables/business-process-logger';
import { useDocumentHelpers } from '@/composables/document-helpers';
import { sortAlphabetically, getBoxImageProxyUrl } from '@/composables/utils';

import { accountMixins } from '@/mixins/account.mixins';
import { mappersMixins } from '@/mixins/mappers.mixins';

import shoeProductionCodeImage from '../assets/img/shoe-production-code.png';
import apparelProductionCodeImage from '../assets/img/apparel-accessory-production-code.png';

const MAX_IMAGES_TO_BE_UPLOADED = 3;
const WARRANTY_IMAGES_REQUIRED_LEGAL_ENTITIES: string[] = [LegalEntityEnum.ON_AG, LegalEntityEnum.ON_UK];

enum TranslationType {
  PLACEHOLDER = 'placeholder',
  LABEL = 'label',
}

export default defineComponent({
  name: 'view-case',
  components: {
    AgGridVue,
    CaseLineImageInput,
    BackstageTextarea,
    ReturnCaseForm,
    BackstageInputLabel,
    BackstageInput,
    BackstageInputCalendar,
    BackstageIcon,
    BackstageCombobox,
    BackstageImageTooltip,
    VeeForm,
    VeeField,
  },
  props: {
    id: {
      type: String,
      default: undefined,
    },
    caseType: {
      type: String,
    },
  },
  setup() {
    const { selectedAccount, selectedAccountId } = storeToRefs(useUserStore());
    const { userLanguage } = useLocale();
    const { addEditingCases } = useAddEditCaseStore();
    const { editingCases } = storeToRefs(useAddEditCaseStore());
    const router = useRouter();
    const {
      dateRequiredValidation,
      numberValidationRequiredMinMaxValue,
      numberValidationMaxValue,
      textMaxCharsValidation,
      textRequiredValidation,
      textRequiredMaxCharsValidation,
      textRequiredMinMaxCharsValidation,
      phoneNumberValidation,
      emailValidation,
    } = useFormValidation();
    const { getTranslationFromTranslateField, formatDate } = useFormat();
    const { defaultGridOptions } = useGrid();
    const { pageViewBPLog } = useBPLogger();
    const { generateFileSHA256 } = useDocumentHelpers();

    return {
      router,
      editingCases,
      userLanguage,
      CaseLineFields,
      TranslationType,
      selectedAccount,
      selectedAccountId,
      shoeProductionCodeImage,
      apparelProductionCodeImage,
      generateFileSHA256,
      formatDate,
      addEditingCases,
      emailValidation,
      defaultGridOptions,
      sortAlphabetically,
      getBoxImageProxyUrl,
      phoneNumberValidation,
      dateRequiredValidation,
      textMaxCharsValidation,
      textRequiredValidation,
      numberValidationMaxValue,
      textRequiredMaxCharsValidation,
      getTranslationFromTranslateField,
      textRequiredMinMaxCharsValidation,
      numberValidationRequiredMinMaxValue,
      pageViewBPLog,
    };
  },
  data() {
    return {
      savingQueue: [] as (() => Promise<any>)[],
      maxImageCount: MAX_IMAGES_TO_BE_UPLOADED,
      isValidCase: false,
      saving: false,
      loading: true,
      casesService: new CasesService(),
      caseOrder: new Case(),
      defectReasons: new Array<DefectReason>(),
      factoryCodesMap: new Map<ProductMainGroups, FactoryCode[]>(),
      productionCodesMap: new Map<ProductMainGroups, ProductionCode[]>(),
      distancesRun: DistancesRun,
      productMainGroups: ProductMainGroups,
      purchaseDate: undefined as unknown as Date,
      defaultColDef: {
        resizable: true,
        sortable: true,
        minWidth: 80,
      },
      gridOptions: this.defaultGridOptions(),
      gridApi: undefined as GridApi | undefined,
      modules: [ClientSideRowModelModule],
      isValidPickupData: false,
      validationSchema: {},
      linesImageHashes: new Map<string, Map<string, string>>(),
    };
  },
  computed: {
    formattedDefectReasons() {
      return this.defectReasons.map((defectReason) => ({
        ...defectReason,
        description: this.getTranslationFromTranslateField(defectReason.description),
      }));
    },
    isWarranty(): boolean {
      return this.caseType === CaseType.Warranty;
    },
    isReturn(): boolean {
      return this.caseType === CaseType.Return;
    },
    isValidPickupReturnForm(): boolean {
      return !this.isPickupRequest() || this.isValidPickupData;
    },
    warrantyImagesRequired(): boolean {
      return this.isWarranty && WARRANTY_IMAGES_REQUIRED_LEGAL_ENTITIES.includes(this.selectedAccount?.legalEntity);
    },
    returnColumnDefinitions(): any[] {
      return [
        { headerValueGetter: () => this.$t('viewCase.item_number'), field: 'productNumber' },
        { headerValueGetter: () => this.$t('viewCase.item_product_group'), field: 'productGroupName' },
        { headerValueGetter: () => this.$t('viewCase.item_quantity'), field: 'quantity' },
      ];
    },
    labelRequestParcelCountOptions() {
      const maxParcelCount = 10;

      return Array(maxParcelCount)
        .fill(0)
        .map((_, index) => index + 1);
    },
    caseNotSubmitted(): boolean {
      return this.caseOrder.status === CaseStatus.NotSubmitted;
    },
  },
  watch: {
    isValidPickupReturnForm() {
      this.emitValidCase();
    },
    caseOrder: {
      deep: true,
      handler() {
        this.emitValidCase();
      },
    },
  },
  mounted() {
    // When the form changes we need to retrigger the validation
    this.fetchCase();
    this.setUpEventListeners();
    console.log(this.pageViewBPLog(true, { page_name: 'view-case', case_number: this.caseOrder?.caseNumber }));
  },
  unmounted() {
    this.tearDownEventListeners();
    this.gridApi = undefined;
  },
  methods: {
    ...accountMixins,
    ...mappersMixins,
    disabledDates() {
      return (target: Date) => {
        return target > moment(new Date()).toDate();
      };
    },

    async emitValidCase(): Promise<void> {
      // Manual validation for warranty cases
      let areImagesProvided = this.warrantyImagesRequired
        ? this.caseOrder.lines.every((line: CaseLine) => this.allImagesAreUploaded(line))
        : this.caseOrder.lines.every((line: CaseLine) => this.allImagesOrCommentProvided(line));
      const areParchedDatesProvided = this.caseOrder.lines.every((line: CaseLine) => line.purchaseDate);

      let isRequiredFieldsFilled;
      try {
        isRequiredFieldsFilled = await (this.$refs as any).form?.validate({ silent: true });
      } catch (error) {
        console.error('Error validating form:', error);
      }

      let manualValidationResults = this.isWarranty
        ? areImagesProvided && areParchedDatesProvided
        : this.isValidPickupReturnForm;

      this.$emit('submit-ready', {
        isValidCase: isRequiredFieldsFilled.valid && manualValidationResults,
        caseOrder: this.caseOrder,
      });
    },

    getTooltipImageByProduct(line: CaseLine) {
      return this.isShoe(line) ? shoeProductionCodeImage : apparelProductionCodeImage;
    },

    isShoe(line: CaseLine) {
      return line.productMainGroup === ProductMainGroups.shoes;
    },

    updatePickUpData(isValidForm: boolean, areChangedFieldsValid: boolean, pickupData: CasePickupModel) {
      this.isValidPickupData = isValidForm;

      this.caseOrder.pickup = pickupData;
      this.caseOrder.reason = pickupData.returnReason || '';
      this.caseOrder.description = pickupData.description;
      this.caseOrder.palletCount = pickupData?.palletCount;
      this.caseOrder.parcelCount = pickupData?.parcelCount;

      if (areChangedFieldsValid) {
        this.onChangeCase();
      }
    },

    isPickupRequest(): boolean {
      if (this.caseOrder.lines) {
        const totalShoeCount = this.caseOrder.lines.reduce((total: number, line: CaseLine) => {
          if (this.isShoe(line)) {
            return total + (line.quantity || 0);
          }

          return total;
        }, 0);
        const returnRequestType =
          returnRequestTypeByLegalEntity[this.selectedAccount?.legalEntity?.toLowerCase()] ??
          returnRequestTypeByLegalEntity.default;

        return (
          this.isReturn &&
          returnRequestType.requestType === CaseReturnRequestType.Pickup &&
          totalShoeCount > returnRequestType.min
        );
      }

      return false;
    },

    setUpEventListeners() {
      window.addEventListener('resize', this.handleResize);
    },
    tearDownEventListeners() {
      window.removeEventListener('resize', this.handleResize);
    },
    allImagesAreUploaded(line: CaseLine): boolean {
      return line.images !== undefined && line.images.length === this.maxImageCount;
    },
    allImagesOrCommentProvided(line: CaseLine): boolean {
      return this.allImagesAreUploaded(line) || !!line.comments;
    },
    hideImagesErrorMessage(line: CaseLine): boolean {
      return this.warrantyImagesRequired ? this.allImagesAreUploaded(line) : true;
    },
    productCodePlaceholder(line: CaseLine) {
      return this.$t(`viewCase.product_code_placeholder_${line.productMainGroup?.toLowerCase()}`).toString();
    },

    getFieldTranslationByType(line: CaseLine, type: TranslationType, field: CaseLineFields) {
      return this.$t(`viewCase.${field}_${type}_${line.productMainGroup?.toLowerCase()}`).toString();
    },

    selectedDefectReasonDescription(line: CaseLine) {
      const selectedDefectReason = this.defectReasons.find(
        (defectReason: DefectReason) =>
          defectReason.productMainGroups.includes(line.productMainGroup) && defectReason.type === line.defectReason,
      );

      return selectedDefectReason ? this.getTranslationFromTranslateField(selectedDefectReason.description) : '-';
    },

    buildValidationSchema() {
      let schema: ObjectShape = {};
      if (this.isWarranty) {
        this.caseOrder.lines.forEach((line) => {
          const lineId = line.id;
          schema[`defect_reason:${lineId}`] = this.textRequiredValidation(`defect_reason:${lineId}`);
          /*
           * NOTE: Validation for purchase date is done manually for now.
           * schema[`purchase_date:${lineId}`] = this.textRequiredValidation(`purchase_date:${lineId}`);
           */
          schema[`product_code:${lineId}`] = this.textRequiredValidation(`product_code:${lineId}`);
          schema[`factory_code:${lineId}`] = this.textRequiredValidation(`factory_code:${lineId}`);

          if (line.productMainGroup === ProductMainGroups.shoes) {
            schema[`distance_run:${lineId}`] = this.textRequiredValidation(`distance_run:${lineId}`);
          }
        });
      } else if (!this.isPickupRequest()) {
        schema = {
          return_reason: this.textRequiredValidation('return_reason'),
          return_parcel_count: this.textRequiredValidation('return_parcel_count'),
          return_description: this.textRequiredValidation('return_description'),
        };
      }

      this.validationSchema = object(schema);
    },

    /**
     * @returns {Promise<void>}
     * @description Fetch the images that were already uploaded to AWS to recalculate their hashes
     * and store them in the linesImageHashes map
     */
    async setLinesImageHashes(): Promise<void> {
      for (const line of this.caseOrder.lines) {
        const lineImageHashes = new Map<string, string>();
        for (const image of line.images || []) {
          const imgSHA256 = (await this.generateFileSHA256(image.imageUrl)) as string;
          lineImageHashes.set(image.imageId, imgSHA256);
        }
        this.linesImageHashes.set(line.id, lineImageHashes);
      }
    },

    async fetchCase() {
      const validatedCaseData = await this.casesService.get(this.id || this.caseOrder.id, this.selectedAccountId);

      if (validatedCaseData.isValid() && validatedCaseData.model) {
        this.caseOrder = this.getMappedCaseOrder(validatedCaseData.model);
        this.$emit('not-submitted', this.caseNotSubmitted);
        await Promise.all([
          this.fetchDefectReasons(this.caseOrder.type),
          this.fetchFactoryCodes(),
          this.fetchProductionCodes(),
        ]);
        this.buildValidationSchema();
      } else {
        this.$emit('can-leave-page');
        this.goBackToCases();
      }

      if (
        this.caseNotSubmitted &&
        !(await this.accountHasCaseCreationEnabled(this.caseType as CaseType, this.selectedAccountId))
      ) {
        this.goBackToCases();
        return;
      }
      await this.setLinesImageHashes();

      this.loading = false;
    },

    goBackToCases() {
      this.$router.push(`/${(this.caseType as CaseType).toLowerCase()}-cases`);
    },

    getMappedCaseOrder(caseResponse: CaseDto | Case): Case {
      const pickupDetails = caseResponse.pickup;
      let pickup = new CasePickupModel();

      if (pickupDetails) {
        pickup = {
          contact: pickupDetails.contact,
          returnReason: caseResponse.reason,
          address: pickupDetails.address,
          addressNotes: pickupDetails.addressNotes,
          customerReference: pickupDetails.customerReference,
          restrictedAccess: pickupDetails.restrictedAccess,
          dimensions: pickupDetails.dimensions,
          totalPackageWeight: pickupDetails.totalPackageWeight,
          singlePackageWeight: pickupDetails.singlePackageWeight,
          tailLiftRequired: pickupDetails.tailLiftRequired,
          weightUnit: pickupDetails.weightUnit,
          pickupDate: new Date(pickupDetails.pickupDate),
          description: caseResponse.description,
          palletCount: caseResponse.palletCount,
          parcelCount: caseResponse.parcelCount,
        };
      }

      const mappedCaseOrder: Case = {
        accountId: caseResponse.accountId,
        caseNumber: caseResponse.caseNumber,
        contactId: caseResponse.contactId,
        createdAt: caseResponse.createdAt,
        id: caseResponse.id,
        isRefund: caseResponse.isRefund,
        reason: caseResponse.reason,
        refundStatus: caseResponse.refundStatus,
        status: caseResponse.status,
        type: caseResponse.type,
        creditNoteNumber: caseResponse.creditNoteNumber,
        description: caseResponse.description,
        itemsOpen: caseResponse.itemsOpen,
        itemsApproved: caseResponse.itemsApproved,
        itemsDeclined: caseResponse.itemsDeclined,
        itemsTotalQuantity: caseResponse.itemsTotalQuantity,
        requestType: caseResponse.requestType,
        palletCount: caseResponse.palletCount,
        parcelCount: caseResponse.parcelCount,
        pickup,

        lines: caseResponse.lines.map((line) => {
          if (!line.images) {
            line.images = new Array<CaseImage>();
          }

          if (line.productMainGroup !== ProductMainGroups.shoes && !line.distanceRun) {
            line.distanceRun = 'NA';
          }

          return { ...line, collapsed: true };
        }),
      };
      return mappedCaseOrder;
    },

    async fetchDefectReasons(caseType: CaseType) {
      const validationResult = await this.casesService.getDefectReasons(caseType, this.selectedAccount.billingCountry);

      if (validationResult.isValid()) {
        this.defectReasons = validationResult.model as DefectReason[];
      }
    },

    categorizeFactoryCodesByMainGroups(factoryCodes: FactoryCode[]) {
      const categorizedFactoryCodes = new Map<ProductMainGroups, FactoryCode[]>();

      factoryCodes.forEach((factoryCode) => {
        factoryCode.productMainGroups.forEach((group) => {
          categorizedFactoryCodes.set(group, (categorizedFactoryCodes.get(group) || []).concat([factoryCode]));
        });
      });

      return categorizedFactoryCodes;
    },

    categorizeProductionCodesByMainGroups(productionCodes: ProductionCode[]) {
      const categorizedProductionCodes = new Map<ProductMainGroups, ProductionCode[]>();

      productionCodes.forEach((productionCode) => {
        productionCode.productMainGroups.forEach((group) => {
          categorizedProductionCodes.set(group, (categorizedProductionCodes.get(group) || []).concat([productionCode]));
        });
      });

      return categorizedProductionCodes;
    },

    async fetchFactoryCodes() {
      const validationResult = await this.casesService.getFactoryCodes();
      if (validationResult.isValid()) {
        this.factoryCodesMap = this.categorizeFactoryCodesByMainGroups(validationResult.model as FactoryCode[]);
      }
    },

    async fetchProductionCodes() {
      const validationResult = await this.casesService.getProductionCodes();

      if (validationResult.isValid()) {
        this.productionCodesMap = this.categorizeProductionCodesByMainGroups(
          validationResult.model as ProductionCode[],
        );
      }
    },

    deleteImage(line: CaseLine, image: CaseImage) {
      this.queueUpdate(async () => {
        const response = await this.casesService.deleteLineImage(
          this.id || this.caseOrder.id,
          line,
          image.imageId,
          this.selectedAccountId,
        );

        if (response.isValid()) {
          this.linesImageHashes.get(line.id)?.delete(image.imageId);
          line.images = line?.images?.filter((obj: CaseImage) => obj.imageId !== image.imageId);
          this.saveLineChangesToStore(line);
        }
      });
    },
    async onChangeImage(line: CaseLine, imageFile: File) {
      let imgSHA256: string;
      imgSHA256 = (await this.generateFileSHA256(imageFile)) as string;
      try {
        this.checkImageSimilarity(imgSHA256, line);
      } catch (error) {
        this.$snotify.error(this.$t('viewCase.similar_image_upload'));
        return;
      }

      return this.queueUpdate(async () => {
        const response = await this.casesService.addLineImage(
          this.id || this.caseOrder.id,
          line,
          imageFile,
          this.selectedAccountId,
        );

        if (response.isValid()) {
          const image = response.model as CaseImage;

          const lineImageHashes = this.linesImageHashes.get(line.id) || new Map<string, string>();
          lineImageHashes.set(image.imageId, imgSHA256);
          this.linesImageHashes.set(line.id, lineImageHashes);

          line?.images?.push(image);

          this.saveLineChangesToStore(line);
        }
      });
    },
    checkImageSimilarity(imgSHA256: string, line: CaseLine): void {
      const lineImageHashes = this.linesImageHashes.get(line.id) || new Map<string, string>();
      const isTooSimilar = [...lineImageHashes.values()].some((existingSHA256) => existingSHA256 === imgSHA256);

      if (isTooSimilar) {
        throw new Error('Uploaded image already exists');
      }
    },
    saveLineChangesToStore(updatedLine: CaseLine) {
      const lineIndex = this.caseOrder?.lines.findIndex(
        (caseOrderLine: CaseLine) => caseOrderLine.id === updatedLine.id,
      );

      this.caseOrder?.lines.splice(lineIndex, 1, updatedLine);
      this.addEditingCases([this.caseOrder]);
      this.caseOrder = this.editingCases[0];
    },
    onChangeLine(line: CaseLine) {
      this.queueUpdate(async () => {
        await this.casesService.updateLine(this.id || this.caseOrder.id, line, this.selectedAccountId);
      });
      this.saveLineChangesToStore(line);
    },
    async deleteLine(line: CaseLine) {
      this.loading = true;
      const response = await this.casesService.deleteLine(this.id || this.caseOrder.id, line, this.selectedAccountId);

      if (response.isValid()) {
        this.caseOrder.lines = this.caseOrder.lines.filter((obj: CaseLine) => obj !== line);
      }

      this.loading = false;
      this.buildValidationSchema();
      this.emitValidCase();
      this.$emit('delete-line', line.productId);
    },
    toggleLine(line: CaseLine) {
      line.collapsed = !line.collapsed;
    },
    onChangeCase() {
      this.queueUpdate(async () => this.updateCase());
    },
    async updateCase() {
      await this.casesService.update(
        this.caseOrder.id,
        this.mapCaseUpdate(this.caseOrder, this.isPickupRequest()),
        this.selectedAccountId,
      );

      this.addEditingCases([this.caseOrder]);
    },
    async queueUpdate(updateCodeBlock?: () => Promise<ValidatorResult<Case | CaseLine> | void>) {
      // if we are already saving we queue the update
      if (this.saving) {
        if (updateCodeBlock) {
          this.savingQueue.unshift(updateCodeBlock);
        }

        return;
      }

      this.saving = true;

      // if no code block we try to pop last queued code block
      if (!updateCodeBlock) {
        updateCodeBlock = this.savingQueue.pop();
      }

      try {
        if (updateCodeBlock) {
          await updateCodeBlock();
        } else {
          console.error('error trying to pop a queed save case task');
        }
      } catch (err) {
        console.error(err);
      }

      this.saving = false;

      if (this.savingQueue.length > 0) {
        this.queueUpdate();

        return;
      }
    },

    onGridReady(grid: any) {
      this.gridApi = grid.api;
      this.gridApi?.sizeColumnsToFit();
    },
    handleResize() {
      this.gridApi?.sizeColumnsToFit();
    },

    ensureNALast(options: string[]) {
      const naIndex = options.indexOf('N/A');

      if (naIndex !== -1) {
        options.splice(naIndex, 1);
        options.push('N/A');
      }

      return options;
    },

    getProductionCodeOptions(productMainGroup: ProductMainGroups) {
      const productCodesByMainGroup = this.productionCodesMap.get(productMainGroup) ?? [];
      const productCodes = productCodesByMainGroup.map((productCode) => {
        return productCode.type;
      });
      return this.ensureNALast(sortAlphabetically(productCodes));
    },

    getFactoryCodeOptions(productMainGroup: ProductMainGroups) {
      const factoryCodesByMainGroup = this.factoryCodesMap.get(productMainGroup) ?? [];
      const factoryCodes = factoryCodesByMainGroup.map((factoryCode) => {
        return factoryCode.type;
      });
      return this.ensureNALast(sortAlphabetically(factoryCodes));
    },

    getDistanceRunOptions() {
      return this.ensureNALast(
        this.distancesRun.map((distance) => {
          return distance.description;
        }),
      );
    },

    getDefectReasonsOptions(productMainGroup: ProductMainGroups) {
      const filteredDefectedReasons = this.defectReasons.filter((defectReason: DefectReason) =>
        defectReason.productMainGroups.includes(productMainGroup),
      );
      const filteredDefectedReasonTypes = filteredDefectedReasons.map((reasons) => {
        return this.getTranslationFromTranslateField(reasons.description);
      });
      return this.ensureNALast(sortAlphabetically(filteredDefectedReasonTypes));
    },

    setDefectType(line: CaseLine) {
      const defectReason = this.defectReasons.find(
        (defectReason: DefectReason) =>
          defectReason.productMainGroups.includes(line.productMainGroup) &&
          Object.values(defectReason.description).includes(line.defectReason),
      );

      if (defectReason) {
        line.defectReason = defectReason.type;
      }
    },
  },
});
</script>

<style scoped lang="scss">
.view-case {
  @include common-container;
  padding: 0 20px;
  margin-top: 1.5rem;

  .case-container {
    background-color: var(--bg-page-color);

    .case-header {
      display: flex;
      align-items: center;
      background-color: var(--primary-bg-color);
      border-radius: 0.25rem 0.25rem 0 0;
      padding: 1rem 1.25rem;
      gap: 16px;

      .case-header-section {
        width: 100%;
        text-align: left;

        h4 {
          line-height: 150%;
          margin: 0 0 0.25rem;
          font-weight: 700;
          font-size: 1rem;
        }

        span {
          line-height: 150%;
          color: var(--primary-text-remark-color);
          font-weight: 400;
          font-size: 0.875rem;
        }
      }
    }

    .products-list {
      background-color: var(--bg-page-color);
      border-radius: 0.25rem 0.25rem 0 0;
      overflow: visible;

      .product {
        padding: 0.75rem 1.25rem;
        background-color: var(--primary-bg-color);
        margin-bottom: 0.0625rem;

        .product-overview {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          cursor: pointer;

          .customer-name {
            width: 34.3%;
            padding-left: 20px;
          }

          .defect-reason {
            padding-left: 20px;
            width: 29%;
          }

          .product-info {
            display: flex;
            align-items: center;
            gap: 1.25rem;
            width: 33%;

            .image {
              width: 5rem;
              height: 5rem;

              img {
                height: 100%;
                width: 100%;
                object-fit: contain;
              }
            }
          }

          h6 {
            text-align: left;
            color: var(--primary-text-color);
            font-size: 1rem;
            font-weight: 700;
            margin: 0;
          }

          p {
            text-align: left;
            font-size: 0.875rem;
            font-weight: 400;
            margin: 0;
          }

          .delete-line {
            width: 1.5rem;
            height: 1.5rem;
            margin-left: auto;
          }
        }

        .product-details {
          max-height: 0;
          overflow: hidden;
          transition:
            max-height 0.5s ease,
            padding 0.5s ease;
        }

        &.expanded {
          .product-details {
            margin: auto;
            padding: 0.625rem 0;
            overflow: visible;
            max-height: fit-content;

            .form-container {
              display: flex;
              align-items: flex-start;
              gap: 3%;

              .col {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 0.75rem;
                width: 100%;

                > * {
                  width: 100%;
                }
              }

              select {
                padding: 0 2.1875rem 0 0;
                background-size: 1.0625rem;
              }

              .has-tooltip {
                position: relative;
                cursor: pointer;

                &:hover {
                  .tooltip {
                    display: block;
                  }
                }

                .tooltip {
                  position: absolute;
                  padding: 0;
                  z-index: 9;
                  top: 50%;
                  left: 100%;
                  display: none;
                  width: 26rem;
                  height: 26rem;
                  transform: translateY(-10%);
                  box-shadow: 0 1.25rem 2.5rem -0.875rem rgba(0, 0, 0, 0.25);
                  background-position: center;
                  background-size: contain;
                  background-repeat: no-repeat;
                  background-color: var(--white-color);
                }
              }

              .images-group {
                width: 100%;
                text-align: left;

                .images-label {
                  font-size: 0.875rem;
                  font-weight: 400;
                }

                .image-upload-container {
                  display: flex;
                  margin-top: 0.75rem;

                  span {
                    width: 100%;
                    display: flex;
                    justify-content: space-between;
                  }
                }
              }
            }
          }
        }

        &:last-child {
          border-radius: 0 0 0.25rem 0.25rem;
        }
      }
    }

    .return-case-cnt {
      @include container-background;
      margin: 0;
      border-radius: 0 0 0.25rem 0.25rem;

      .return-case-options {
        display: flex;
        align-items: flex-start;
        flex-grow: 1;
        gap: 12px;

        .return-case-option-item {
          width: 100%;
        }

        .field-new select {
          width: 100%;
        }
      }
    }

    .return-items {
      @include container-background;
      margin-top: 48px;
    }

    .buttons-container {
      padding: 1.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 1.5625rem;
      margin-bottom: 1rem;
    }
  }
}

@include breakpoint(medium) {
  .view-case {
    .form-container {
      flex-wrap: wrap;

      .col {
        margin-bottom: 0.75rem;
      }
    }

    .case-container .products-list .product .product-overview {
      div.product-info {
        width: 90%;
      }

      .defect-reason,
      .status,
      .customer-name {
        display: none;
      }
    }

    .buttons-container {
      flex-wrap: wrap;
    }
  }
}
</style>

<style lang="scss">
.view-case {
  .form-container {
    input {
      &:disabled {
        cursor: initial;
      }
    }

    select {
      width: 100%;

      &:disabled {
        cursor: initial;
        color: var(--field-text-disabled-color);
      }
    }

    .comment-group span {
      width: 100%;

      p,
      textarea {
        width: 100%;
        resize: none;
        text-align: left;

        @include breakpoint(untillarge) {
          min-width: 100%;
        }
      }

      textarea {
        height: 1rem;

        &:disabled {
          cursor: initial;
        }
      }
    }
  }

  .form-container,
  .return-case-cnt {
    select {
      &.option-placeholder {
        color: var(--primary-text-remark-color);
      }

      option {
        color: var(--primary-text-color);
      }
    }

    .field-new textarea {
      min-height: 89px;
      width: 100%;
      resize: vertical;
    }

    .label-request-input {
      margin: 0px 0px 10px 0px;
    }

    .label-request-textarea {
      height: 130px;
      max-height: 130px;
      margin: 0;
      resize: none;
    }
  }
}
</style>
